import React from "react";
import PropTypes from 'prop-types';

import SnackbarContext from "./SnackbarContext";
import Snackbar from '../Snackbar/Snackbar';
import Button from '../Button/Button';

interface SnackbarProviderProps {
    children: React.ReactNode;
}

// interface SnackbarProviderItem {
//     message: string,
//     type: 'success' | 'informational' | 'error' | 'warning',
//     onClose: () => void,
// }

interface SnackbarProviderItem {
    primaryMessage: string,
    secondaryMessage: string,
    type: 'success' | 'informational' | 'error' | 'warning',
    onClose: () => void,
    buttonText: string,
    onButtonClick: () => void,
}

interface SnackbarProviderState {
    snackbars: SnackbarProviderItem[],
    hideLead: boolean,
}

/**
 * A wrapper for an application to allow creating snackbars via a command.
 * 
 * ## _
 * ### Arguments
 * @param {React.ReactNode} children - Application to be wrapped in snackbariness.
 * 
 * ## _
 * ### Functions
 * @param {function} createSnackbar - Creates a snackbar. Needs a message, a type, and an optional onClose function call.
 * @param {function} createSuccessSnackbar - Creates a success snackbar. Needs a message and an optional onClose function call.
 * @param {function} createInfoSnackbar - Creates an informational snackbar. Needs a message and an optional onClose function call.
 * @param {function} createWarningSnackbar - Creates a warning snackbar. Needs a message and an optional onClose function call.
 * @param {function} createErrorSnackbar - Creates an error snackbar. Needs a message and an optional onClose function call.
 */
class SnackbarProvider extends React.Component<SnackbarProviderProps, SnackbarProviderState> {
    static propTypes = {
        children: PropTypes.node.isRequired,
    }

    // snackbars - list of snackbars to be shown. Will be shown in list order.
    // hideLead - some jank to make sure the animations play. Will toggle visibility for the snackbar currently being shown.
    state: SnackbarProviderState = {
        snackbars: [],
        hideLead: false,
    };

    createSnackbar = (
        primaryMessage: string, 
        secondaryMessage: string, 
        type: ('success' | 'informational' | 'error' | 'warning'), 
        onClose: () => void = () => {}, 
        buttonText: string, 
        onButtonClick: () => void = () => {}
    ) => {
        // Append the snackbar to the "queue" of snackbars.
        this.setState((prevState) => ({
            snackbars: [...prevState.snackbars, {primaryMessage, secondaryMessage, type, onClose, buttonText, onButtonClick}],
        }));
    }

    createSuccessSnackbar = (primaryMessage: string, secondaryMessage: string, onClose: () => void, buttonText: string, onButtonClick: () => void) => {
        return this.createSnackbar(primaryMessage, secondaryMessage, "success", onClose, buttonText, onButtonClick);
    }

    createInfoSnackbar = (primaryMessage: string, secondaryMessage: string, onClose: () => void, buttonText: string, onButtonClick: () => void) => {
        return this.createSnackbar(primaryMessage, secondaryMessage, "informational", onClose, buttonText, onButtonClick);
    }

    createWarningSnackbar = (primaryMessage: string, secondaryMessage: string, onClose: () => void, buttonText: string, onButtonClick: () => void) => {
        return this.createSnackbar(primaryMessage, secondaryMessage, "warning", onClose, buttonText, onButtonClick);
    }

    createErrorSnackbar = (primaryMessage: string, secondaryMessage: string, onClose: () => void, buttonText: string, onButtonClick: () => void) => {
        return this.createSnackbar(primaryMessage, secondaryMessage, "error", onClose, buttonText, onButtonClick);
    }

    clearSnackbars = () => {
        this.setState(() => ({
            snackbars: [],
            hideLead: false,
        }))
    }

    removeSnackbar = () => {
        this.setState((prevState) => ({
            snackbars: prevState.snackbars.slice(1),
            hideLead: false,
        }))
    }

    render() {
        const { children } = this.props;

        return (
            <SnackbarContext.Provider value={{ 
                clearSnackbars: this.clearSnackbars,
                createSnackbar: this.createSnackbar,
                createSuccessSnackbar: this.createSuccessSnackbar, 
                createWarningSnackbar: this.createWarningSnackbar,
                createErrorSnackbar: this.createErrorSnackbar,
                createInfoSnackbar: this.createInfoSnackbar,
            }}>
                {children}
                {this.state.snackbars.map((snackbar, index) => (
                    // Create every snackbar. Only make the first one visible. As the snackbars leave the queue, the next will be shown.
                    <Snackbar 
                        key={`fusion-snackbar-${index}`}
                        isHidden={index != 0 || this.state.hideLead}
                        type={snackbar.type}
                        onClose={() => {
                            // This is on a timeout to allow the animation of the snackbar disappearing to play.
                            // Once the animation is complete, the snackbar is removed via the removeSnackbar function.
                            this.setState({hideLead: true});
                            // Note - in some cases, there will be an empty or null onClose function. 
                            // In lieu of making the front-end designer make a shell function, this will be nullable.
                            if (snackbar.onClose) {
                                snackbar.onClose();
                            }
                            setTimeout(() => this.removeSnackbar(), 500);
                        }}
                        primaryMessage={snackbar.primaryMessage}
                        secondaryMessage={snackbar.secondaryMessage}
                    >
                        { snackbar.buttonText &&
                            <Button
                                children={snackbar.buttonText}
                                onClick={snackbar.onButtonClick}
                            />
                        }
                    </Snackbar>
                ))}
            </SnackbarContext.Provider>
        );
    };
}

export default SnackbarProvider;